<template>
    <div class="container-fluid default-dashboard">
        <div class="card">
            <div class="card-header">
                <div class="d-flex justify-content-between align-items-center">
                    <h5>Role List</h5>
                    <button class="btn btn-primary" @click="openModal()">
                        <i class="fa fa-plus"></i> Add Role
                    </button>
                </div>
            </div>
            <div class="card-body">
                <DataTable
                    :headers="headers"
                    :items="roles"
                    :loading="loading"
                    :total="total"
                    :params="params"
                    @update:params="updateParams"
                >
                    <template #item.actions="{ item }">
                        <button class="btn btn-sm btn-warning me-2" @click="editRole(item as any)">
                            <i class="fa fa-edit"></i>
                        </button>
                        <button class="btn btn-sm btn-danger" @click="deleteRole(item as any)">
                            <i class="fa fa-trash"></i>
                        </button>
                    </template>
                </DataTable>
            </div>
        </div>
    </div>

    <div class="modal fade bd-example-modal-lg" id="roleModal" tabindex="-1" ref="modalRef" aria-labelledby="roleModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-lg" role="document">
            <div class="modal-content">
                <h3 class="modal-header justify-content-center border-0">{{ modalTitle }}</h3>
                    <div class="modal-body">
                        <div class="row">
                            <div class="col-md-6 mb-2">
                                <label class="form-label col-form-label">Name</label>
                                <input v-model="form.name" type="text" class="form-control" required>
                            </div>
                            <div class="col-md-6 mb-2">
                                <label class="form-label col-form-label">Description</label>
                                <input v-model="form.description" type="text" class="form-control" required>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button class="btn btn-danger" type="button" @click="closeModal">Cancel</button>
                        <button class="btn btn-primary" type="submit" @click="saveRole">Save</button>
                    </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, reactive, onMounted } from 'vue'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import DataTable from '@/components/table/Datatable.vue'
import { Modal } from 'bootstrap'

const roles = ref<any[]>([])
const loading = ref(false)
const total = ref(0)
const modalTitle = ref('')
const params = reactive<DataTableParams>({
    page: 1,
    size: 10,
    search: ''
})

const headers = [
    { text: 'Name', value: 'name' },
    { text: 'Description', value: 'description' },
    { text: 'Actions', value: 'actions' }
]

const initForm = {
    ID: null,
    name: '',
    description: '',
}

const form = reactive({
    ...initForm
})

const dataRoles: any[] = []
import * as setting from "@/api/services/SettingRequest";

const fetchRoles = async () => {
    loading.value = true
    try {
        const resp = await setting.postRequest("/role/list", {...params});
        if(resp.status !== 200) throw new Error('Failed to fetch roles')
        roles.value = resp.data
        total.value = resp.total
    } catch (error) {
        toast.error('Failed to fetch roles')
    }
    loading.value = false
}

const updateParams = (newParams: Partial<DataTableParams>) => {
    Object.assign(params, newParams)
    fetchRoles()
}

const modalRef = ref<HTMLElement | null>(null)
let bsModal: any | null = null

onMounted(() => {
    if (modalRef.value) {
        bsModal = new Modal(modalRef.value)
    }
    fetchRoles()
})

const closeModal = () => {
    if (bsModal) {
        bsModal.hide()
    }
}

const editRole = (role: any) => {
    openModal(role);
}

const saveRole = async () => {
    loading.value = true
    try {
        if (form.ID) {
            await setting.postRequest("/role/update", form).then((resp) => {
                if (resp.status === 200) {
                    toast.success('Role updated successfully');
                    closeModal();
                    fetchRoles();
                } else {
                    toast.error('Failed to update role');
                }
            }).catch((error) => {
                toast.error(error.message || 'Failed to update role');
            })
        } else {
            await setting.postRequest("/role/create", form).then((resp) => {
                if (resp.status === 200) {
                    toast.success('Role created successfully');
                    closeModal();
                    fetchRoles();
                } else {
                    toast.error('Failed to create role');
                }
            }).catch((error) => {
                toast.error(error.message || 'Failed to create role');
            })
        }
    } catch (error: any) {
        toast.error(error.message || 'Failed to save role');
    } finally {
        loading.value = false
    }
}

const deleteRole = async (role: any) => {
    if (!confirm('Are you sure you want to delete this role?')) return

    loading.value = true
    try {
        await setting.postRequest("/role/delete", {ID: role.ID}).then((resp) => {
            if (resp.status === 200) {
                toast.success('Role deleted successfully');
                closeModal();
                fetchRoles();
            } else {
                toast.error('Failed to delete role');
            }
        })

    } catch (error: any) {
        toast.error(error.message || 'Failed to delete role');
    } finally {
        loading.value = false
    }
}

const openModal = (role: any | null = null) => {
    if (role) {
        modalTitle.value = 'Edit Role'
        Object.assign(form, role);
    } else {
        modalTitle.value = 'Create Role'
        Object.assign(form, {...initForm})
    }
    if (bsModal) {
        bsModal.show()
    }
}
</script>